import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface BoardFilterOption {
    id: string;
    label: string;
    color?: string;
    meta?: string;
    iconUrl?: string | null;
    platform?: string;
}

interface BoardFilterDropdownProps {
    label: string;
    options: BoardFilterOption[];
    selected: string[];
    onChange: (next: string[]) => void;
    mode?: 'multi' | 'single';
    showCheckbox?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    align?: 'start' | 'end';
    emptyText?: string;
    clearLabel?: string;
    applyFooter?: boolean;
    loading?: boolean;
    className?: string;
}

export function BoardFilterDropdown({
    label,
    options,
    selected,
    onChange,
    mode = 'multi',
    showCheckbox = true,
    searchable = true,
    searchPlaceholder,
    align = 'start',
    emptyText = 'No options',
    clearLabel = 'Clear',
    applyFooter = false,
    loading = false,
    className,
}: BoardFilterDropdownProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [draft, setDraft] = useState<string[]>(selected);

    useEffect(() => {
        if (open) setDraft(selected);
    }, [open, selected]);

    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const activeCount = selected.length;

    const commit = (next: string[]) => {
        onChange(next);
        setOpen(false);
    };

    const toggleDraft = (id: string) =>
        setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const handleRowClick = (id: string) => {
        if (mode === 'single' && applyFooter) {
            setDraft((prev) => (prev.includes(id) ? [] : [id]));
        } else if (mode === 'single') {
            commit(selected.includes(id) ? [] : [id]);
        } else {
            toggleDraft(id);
        }
    };

    const usesDraft = mode === 'multi' || applyFooter;
    const showApply = usesDraft;
    const canClear = usesDraft ? draft.length > 0 || activeCount > 0 : activeCount > 0;
    const menuLike = mode === 'single' && !showCheckbox && !searchable && !applyFooter;
    const showFooter = showApply || (canClear && !menuLike);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex items-center gap-1.5 rounded-lg border px-2.5 h-9 text-[12.5px] font-medium transition shrink-0',
                        activeCount > 0
                            ? 'border-brand bg-brand-subtle text-brand-cta'
                            : 'border-[#E4E7EC] bg-white text-[#344054] hover:bg-[#F9FAFB]',
                        className,
                    )}
                >
                    {label}
                    {activeCount > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-brand text-white text-[10px] font-semibold min-w-[16px] h-4 px-1">
                            {activeCount}
                        </span>
                    )}
                    <ChevronDown
                        className={cn('w-3.5 h-3.5 text-[#98A2B3] transition-transform', open && 'rotate-180')}
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align={align}
                sideOffset={6}
                className={cn(
                    'p-0 rounded-xl bg-white border border-[#E4E7EC] shadow-[0_8px_20px_rgba(16,24,40,0.08)]',
                    menuLike ? 'w-40 min-w-[8rem]' : 'w-[260px]',
                )}
            >
                {searchable && options.length > 6 && (
                    <div className="p-2.5 border-b border-[#F2F4F7]">
                        <div className="flex items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 py-1.5">
                            <Search className="w-3.5 h-3.5 text-[#98A2B3] shrink-0" />
                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder || `Search ${label.toLowerCase()}…`}
                                className="flex-1 bg-transparent outline-none text-[12.5px] text-[#101828] placeholder:text-[#98A2B3] min-w-0"
                            />
                        </div>
                    </div>
                )}

                <div className={cn('max-h-[280px] overflow-y-auto scrollbar-thin', menuLike ? 'p-1' : 'p-1.5')}>
                    {loading ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">Loading…</p>
                    ) : options.length === 0 ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">{emptyText}</p>
                    ) : filtered.length === 0 ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">No matches</p>
                    ) : (
                        filtered.map((o) => {
                            const checked = usesDraft ? draft.includes(o.id) : selected.includes(o.id);
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => handleRowClick(o.id)}
                                    className={cn(
                                        'w-full flex items-center text-left transition',
                                        menuLike
                                            ? cn(
                                                  'gap-2 rounded-md px-2 py-1.5 text-[13px] font-medium text-[#101828]',
                                                  checked ? 'bg-[#F9FAFB]' : 'hover:bg-[#F9FAFB]',
                                              )
                                            : cn(
                                                  'gap-2.5 px-2 py-1.5 rounded-lg',
                                                  checked ? 'bg-brand-subtle' : 'hover:bg-[#F9FAFB]',
                                              ),
                                    )}
                                >
                                    {showCheckbox ? (
                                        <span
                                            className={cn(
                                                'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition',
                                                checked ? 'bg-brand border-brand' : 'border-[#D0D5DD] bg-white',
                                            )}
                                        >
                                            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                        </span>
                                    ) : null}
                                    {o.color && (
                                        <span
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ background: o.color }}
                                        />
                                    )}
                                    {o.iconUrl ? (
                                        <img
                                            src={o.iconUrl}
                                            alt=""
                                            className="h-5 w-5 shrink-0 rounded-full object-cover"
                                        />
                                    ) : null}
                                    <span
                                        className={cn(
                                            'flex-1 min-w-0 truncate',
                                            menuLike
                                                ? undefined
                                                : 'text-[13px] font-medium text-[#101828]',
                                        )}
                                    >
                                        {o.label}
                                    </span>
                                    {o.meta && <span className="text-[11px] text-[#98A2B3] shrink-0">{o.meta}</span>}
                                    {!showCheckbox && checked ? (
                                        <Check
                                            className={cn(
                                                'h-3.5 w-3.5 shrink-0',
                                                menuLike ? 'text-[#6B7280]' : 'text-brand',
                                            )}
                                            strokeWidth={3}
                                        />
                                    ) : null}
                                </button>
                            );
                        })
                    )}
                </div>

                {showFooter && (
                    <div className="border-t border-[#F2F4F7] p-2 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => commit([])}
                            disabled={!canClear}
                            className={cn(
                                'text-[12.5px] font-semibold px-2.5 h-8 rounded-lg transition',
                                canClear
                                    ? 'text-[#475467] hover:bg-[#F9FAFB]'
                                    : 'text-[#D0D5DD] cursor-not-allowed',
                            )}
                        >
                            {clearLabel}
                        </button>
                        {showApply && (
                            <button
                                type="button"
                                onClick={() => commit(draft)}
                                className="text-[12.5px] font-semibold px-3 h-8 rounded-lg bg-brand hover:bg-brand-hover text-white transition"
                            >
                                Apply
                            </button>
                        )}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
